import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Building, BookOpen, Calendar, Shield, Calculator, Briefcase, Users,
  UserCircle, BadgePercent, Sparkles, ChevronRight, CheckCircle, Circle,
  AlertCircle, ArrowRight, Settings2, Save, RefreshCw, Plus, X, Trash2, Tractor,
  Clock, Play, FileText, DollarSign, Upload, Download, Loader2, CheckCircle2,
  AlertTriangle, Eye, Send, Brain, Zap, ListChecks, Receipt, Printer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCompany } from '../context/CompanyContext';
import { toast } from 'sonner';
import TaxTableManager from '../components/TaxTableManager';
import { ChartOfAccounts } from './ViewPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SetupPage = () => {
  const location = useLocation();
  const path = location.pathname;

  const getContent = () => {
    if (path.includes('/setup/company')) return <CompanySetup />;
    if (path.includes('/setup/account-design')) return <AccountDesign />;
    if (path.includes('/setup/fiscal-year')) return <FiscalYear />;
    if (path.includes('/setup/security')) return <SecuritySetup />;
    if (path.includes('/setup/costing')) return <CostingOptions />;
    if (path.includes('/setup/accounts')) return <ChartOfAccounts backTo="/setup" />;
    if (path.includes('/setup/enterprises')) return <EnterprisesSetup />;
    if (path.includes('/setup/vendors-customers')) return <VendorsCustomersSetup />;
    if (path.includes('/setup/deductions')) return <DeductionsSetup />;
    if (path.includes('/setup/tax-tables')) return <TaxTablesSetup />;
    if (path.includes('/setup/employees')) return <EmployeesSetup />;
    if (path.includes('/setup/ai-configure')) return <AIConfigureSetup />;
    if (path.includes('/setup/ca-farm-payroll')) return <CAFarmPayrollSetup />;
    return <SetupWizard />;
  };

  return <div className="space-y-6">{getContent()}</div>;
};

// Setup Wizard Overview
const SetupWizard = () => {
  const navigate = useNavigate();
  
  const setupSteps = [
    { 
      category: 'Company Setup',
      icon: Building,
      steps: [
        { id: 'company', label: 'Name & Address', path: '/setup/company', status: 'complete' },
        { id: 'account-design', label: 'Account Design', path: '/setup/account-design', status: 'complete' },
        { id: 'fiscal-year', label: 'Fiscal Year', path: '/setup/fiscal-year', status: 'complete' },
        { id: 'security', label: 'Passwords & Security', path: '/setup/security', status: 'pending' },
        { id: 'costing', label: 'Costing Options', path: '/setup/costing', status: 'pending' },
        { id: 'ca-farm-payroll', label: 'CA Farm Payroll', path: '/setup/ca-farm-payroll', status: 'pending', highlight: true },
      ]
    },
    {
      category: 'Accounts & Structure',
      icon: BookOpen,
      steps: [
        { id: 'accounts', label: 'Chart of Accounts', path: '/setup/accounts', status: 'complete' },
        { id: 'enterprises', label: 'Enterprises / Operations', path: '/setup/enterprises', status: 'pending' },
        { id: 'vendors-customers', label: 'Vendors & Customers', path: '/setup/vendors-customers', status: 'pending' },
      ]
    },
    {
      category: 'Employer & Payroll',
      icon: Users,
      steps: [
        { id: 'deductions', label: 'Deductions', path: '/setup/deductions', status: 'pending' },
        { id: 'tax-tables', label: 'Tax & Payroll Setup', path: '/setup/tax-tables', status: 'pending' },
        { id: 'employees', label: 'Employees', path: '/setup/employees', status: 'pending' },
      ]
    }
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Setup Wizard</h1>
          <p className="text-gray-500 mt-1">Complete setup in order before entering transactions</p>
        </div>
      </div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Setup Order Matters</h3>
            <p className="text-sm text-amber-700 mt-1">
              Complete each section in order. Later sections depend on earlier setup choices.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {setupSteps.map((section, idx) => {
          const SectionIcon = section.icon;
          const completedCount = section.steps.filter(s => s.status === 'complete').length;
          
          return (
            <Card key={idx}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <SectionIcon className="w-5 h-5 text-slate-600" />
                    </div>
                    <CardTitle className="text-lg">{section.category}</CardTitle>
                  </div>
                  <span className="text-sm text-gray-500">
                    {completedCount}/{section.steps.length} complete
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {section.steps.map((step) => (
                    <button
                      type="button"
                      key={step.id}
                      onClick={() => navigate(step.path)}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                      data-testid={`setup-${step.id}`}
                    >
                      {step.status === 'complete' ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-300" />
                      )}
                      <span className={`flex-1 font-medium ${step.status === 'complete' ? 'text-gray-700' : 'text-gray-500'}`}>
                        {step.label}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI Configure */}
      <Card className="bg-gradient-to-r from-purple-50 to-sky-50 border-purple-200">
        <CardContent className="p-4 flex items-center gap-4">
          <Sparkles className="w-8 h-8 text-purple-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">AI Setup Assistant</h3>
            <p className="text-sm text-gray-600">Let AI ask you questions and configure settings automatically</p>
          </div>
          <Button onClick={() => navigate('/setup/ai-configure')} className="bg-purple-600 hover:bg-purple-700">
            Start AI Setup
          </Button>
        </CardContent>
      </Card>
    </>
  );
};

// ==================== COMPANY SETUP ====================
const BLANK_ADDRESS = { address: '', address2: '', city: '', region: '', postal_code: '', country: '' };
const BLANK_MANAGER = { name: '', title: '', email: '', phone: '', cell: '' };
const BLANK_COMPANY = {
  name: '', dba: '', country: 'US', address: '', address2: '', city: '', region: '', postal_code: '', phone: '', tax_id: '', currency: 'USD',
  email: '', mailing_address: BLANK_ADDRESS, shipping_address: BLANK_ADDRESS, contractor_license: '', naics: '', sic: '', managers: []
};
const selectClass = 'w-full mt-1 p-2 border rounded-md bg-white text-sm';

const CompanySetup = () => {
  const { companyId, selectedCompany, registerCompany } = useCompany();
  const [geo, setGeo] = useState({ countries: [], currencies: ['USD'] });
  const [mode, setMode] = useState('edit');            // 'edit' the company on screen, or 'new' to add another
  const [formData, setFormData] = useState(BLANK_COMPANY);
  const [legacy, setLegacy] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justSaved, setJustSaved] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/company-profile/countries`, { credentials: 'include' });
        if (res.ok) setGeo(await res.json());
      } catch (e) { /* the form still works with the free-text fallbacks */ }
    })();
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !companyId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/company-profile/${encodeURIComponent(companyId)}`, { credentials: 'include' });
        const p = res.ok ? await res.json() : {};
        if (cancelled) return;
        setFormData({ ...BLANK_COMPANY, ...Object.fromEntries(Object.entries(p).filter(([, v]) => v != null)), name: p.name || selectedCompany?.name || '', mailing_address: { ...BLANK_ADDRESS, ...(p.mailing_address || {}) }, shipping_address: { ...BLANK_ADDRESS, ...(p.shipping_address || {}) }, managers: p.managers || [] });
        setLegacy({ client: p.legacy_client_no, path: p.source_path });
      } catch (e) {
        if (!cancelled) setFormData({ ...BLANK_COMPANY, name: selectedCompany?.name || '' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const country = geo.countries.find((c) => c.code === formData.country);
  const set = (field) => (e) => setFormData((f) => ({ ...f, [field]: e.target.value }));
  const setAddr = (kind, field) => (e) => setFormData((f) => ({ ...f, [kind]: { ...BLANK_ADDRESS, ...(f[kind] || {}), [field]: e.target.value } }));
  const copyAddress = (kind) => setFormData((f) => ({
    ...f, [kind]: { address: f.address, address2: f.address2, city: f.city, region: f.region, postal_code: f.postal_code, country: f.country },
  }));
  const setManager = (i, field) => (e) => setFormData((f) => ({ ...f, managers: f.managers.map((m, j) => (j === i ? { ...m, [field]: e.target.value } : m)) }));
  const addManager = () => setFormData((f) => ({ ...f, managers: [...(f.managers || []), { ...BLANK_MANAGER }] }));
  const removeManager = (i) => setFormData((f) => ({ ...f, managers: f.managers.filter((_, j) => j !== i) }));
  const changeCountry = (e) => {
    const c = geo.countries.find((x) => x.code === e.target.value);
    setFormData((f) => ({ ...f, country: e.target.value, region: '', currency: c?.currency || f.currency }));
  };

  const startNew = () => {
    setJustSaved(null);
    setMode('new');
    setFormData({ ...BLANK_COMPANY, country: formData.country, currency: formData.currency });
  };

  const handleSave = async () => {
    if (formData.name.trim().length < 2) { toast.error('Enter the company name'); return; }
    setSaving(true);
    try {
      const creating = mode === 'new';
      const res = await fetch(creating ? `${API_URL}/api/company-profile` : `${API_URL}/api/company-profile/${encodeURIComponent(companyId)}`, {
        method: creating ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Could not save the company. Please try again.');
        return;
      }
      registerCompany(data);
      window.dispatchEvent(new Event('companiesChanged'));
      setJustSaved(creating ? `${data.name} was set up and is now the company on screen.` : 'Company details saved.');
      setMode('edit');
    } catch (err) {
      toast.error('Could not save the company. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const regions = country?.regions;
  const taxId = country?.tax_id_label || 'Tax ID';

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Setup</h1>
          <p className="text-gray-500 mt-1">
            {mode === 'new' ? 'Set up a new company' : <>Details for <b>{selectedCompany?.name || 'this company'}</b></>}
          </p>
        </div>
        {mode === 'edit' && (
          <Button variant="outline" onClick={startNew} data-testid="add-company-btn">
            <Plus className="w-4 h-4 mr-2" />Add another company
          </Button>
        )}
      </div>

      {justSaved && (
        <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 px-4 py-2 rounded-lg" data-testid="company-saved">
          <CheckCircle className="w-5 h-5" /><span>{justSaved}</span>
        </div>
      )}

      <Card>
        <CardContent className="p-6 space-y-4">
          {loading && mode === 'edit' ? (
            <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Company Name *</label>
                  <Input value={formData.name} onChange={set('name')} className="mt-1" data-testid="company-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">DBA / Trade Name</label>
                  <Input placeholder="Optional" value={formData.dba} onChange={set('dba')} className="mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Country *</label>
                  <select className={selectClass} value={formData.country} onChange={changeCountry} data-testid="company-country">
                    {geo.countries.length === 0 && <option value={formData.country}>{formData.country}</option>}
                    {geo.countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Taxes follow the country and {(country?.region_label || 'region').toLowerCase()}.</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Currency</label>
                  <select className={selectClass} value={formData.currency} onChange={set('currency')} data-testid="company-currency">
                    {geo.currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Address</label>
                <Input value={formData.address} onChange={set('address')} className="mt-1" />
                <Input placeholder="Address line 2 (optional)" value={formData.address2} onChange={set('address2')} className="mt-2" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">{country?.city_label || 'City'}</label>
                  <Input value={formData.city} onChange={set('city')} className="mt-1" data-testid="company-city" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{country?.region_label || 'State / Province / Region'}</label>
                  {regions ? (
                    <select className={selectClass} value={formData.region} onChange={set('region')} data-testid="company-region">
                      <option value="">Choose...</option>
                      {regions.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                    </select>
                  ) : (
                    <Input value={formData.region} onChange={set('region')} className="mt-1" data-testid="company-region" />
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{country?.postal_label || 'Postal code'}</label>
                  <Input value={formData.postal_code} onChange={set('postal_code')} className="mt-1" data-testid="company-postal" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <Input value={formData.phone} onChange={set('phone')} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{taxId}</label>
                  <Input value={formData.tax_id} onChange={set('tax_id')} className="mt-1" data-testid="company-tax-id" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Company email</label>
                  <Input type="email" value={formData.email} onChange={set('email')} className="mt-1" data-testid="company-email" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">NAICS code</label>
                  <Input placeholder="e.g. 111331" value={formData.naics} onChange={set('naics')} className="mt-1" data-testid="company-naics" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">SIC code</label>
                  <Input placeholder="e.g. 0172" value={formData.sic} onChange={set('sic')} className="mt-1" data-testid="company-sic" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Contractor license</label>
                <Input placeholder="License number, class and expiry (for contractors)" value={formData.contractor_license} onChange={set('contractor_license')} className="mt-1" data-testid="company-license" />
              </div>

              {[['mailing_address', 'Mailing address'], ['shipping_address', 'Shipping address']].map(([kind, title]) => (
                <div key={kind} className="rounded-md border p-4 space-y-2" data-testid={`company-${kind}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{title}</span>
                    <button type="button" className="text-xs text-sky-700 underline" onClick={() => copyAddress(kind)}>Same as the company address</button>
                  </div>
                  <Input placeholder="Address" value={formData[kind]?.address || ''} onChange={setAddr(kind, 'address')} />
                  <Input placeholder="Address line 2 (optional)" value={formData[kind]?.address2 || ''} onChange={setAddr(kind, 'address2')} />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder={country?.city_label || 'City'} value={formData[kind]?.city || ''} onChange={setAddr(kind, 'city')} />
                    <Input placeholder={country?.region_label || 'State / Province'} value={formData[kind]?.region || ''} onChange={setAddr(kind, 'region')} />
                    <Input placeholder={country?.postal_label || 'Postal code'} value={formData[kind]?.postal_code || ''} onChange={setAddr(kind, 'postal_code')} />
                  </div>
                </div>
              ))}

              <div className="rounded-md border p-4 space-y-3" data-testid="company-managers">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Company managers</span>
                  <Button type="button" variant="outline" size="sm" onClick={addManager} data-testid="add-manager-btn"><Plus className="w-3 h-3 mr-1" />Add manager</Button>
                </div>
                {(formData.managers || []).length === 0 && <p className="text-sm text-gray-500">No managers listed yet.</p>}
                {(formData.managers || []).map((m, i) => (
                  <div key={i} className="grid grid-cols-6 gap-2 items-center">
                    <Input placeholder="Name *" value={m.name} onChange={setManager(i, 'name')} />
                    <Input placeholder="Title" value={m.title || ''} onChange={setManager(i, 'title')} />
                    <Input placeholder="Email" value={m.email || ''} onChange={setManager(i, 'email')} />
                    <Input placeholder="Phone" value={m.phone || ''} onChange={setManager(i, 'phone')} />
                    <Input placeholder="Cell phone" value={m.cell || ''} onChange={setManager(i, 'cell')} />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeManager(i)} aria-label="Remove manager"><X className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>

              {mode === 'edit' && (legacy.client || legacy.path) && (
                <div className="rounded-md bg-slate-50 border p-3 text-sm text-gray-600" data-testid="company-legacy-info">
                  Imported from AgData Blue Skies
                  {legacy.client ? <> as old client number <b>{legacy.client}</b></> : null}
                  {legacy.path ? <> (source: <span className="font-mono">{legacy.path}</span>)</> : null}.
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                {mode === 'new' && <Button variant="outline" onClick={() => setMode('edit')}>Cancel</Button>}
                <Button onClick={handleSave} disabled={saving} data-testid="save-company-btn">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {mode === 'new' ? 'Set up company' : 'Save Changes'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

// ==================== FISCAL YEAR ====================
const FiscalYear = () => {
  const [formData, setFormData] = useState({
    startDate: '2025-07-01',
    endDate: '2026-06-30'
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 500);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fiscal Year Setup</h1>
          <p className="text-gray-500 mt-1">Define your accounting periods</p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-2 rounded-lg">
            <CheckCircle className="w-5 h-5" />Saved!
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Fiscal Year Start</label>
              <Input 
                type="date" 
                className="mt-1" 
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Fiscal Year End</label>
              <Input 
                type="date" 
                className="mt-1" 
                value={formData.endDate}
                onChange={(e) => setFormData({...formData, endDate: e.target.value})}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

// ==================== SECURITY SETUP ====================
const SecuritySetup = () => {
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChangePassword = () => {
    if (passwords.newPass !== passwords.confirm) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    if (passwords.newPass.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswords({ current: '', newPass: '', confirm: '' });
    }, 500);
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Passwords & Security</h1>
        <p className="text-gray-500 mt-1">Configure access controls and passwords</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Current Password</label>
            <Input 
              type="password" 
              value={passwords.current}
              onChange={(e) => setPasswords({...passwords, current: e.target.value})}
              className="mt-1" 
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">New Password</label>
            <Input 
              type="password"
              value={passwords.newPass}
              onChange={(e) => setPasswords({...passwords, newPass: e.target.value})}
              className="mt-1" 
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Confirm New Password</label>
            <Input 
              type="password"
              value={passwords.confirm}
              onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
              className="mt-1" 
            />
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleChangePassword} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

// ==================== EMPLOYEES SETUP ====================
const EmployeesSetup = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [taxTables, setTaxTables] = useState({ us_states: [], west_africa: [] });
  const [newEmp, setNewEmp] = useState({
    name: '',
    employee_id: '',
    position: '',
    hire_date: new Date().toISOString().split('T')[0],
    pay_type: 'hourly',
    pay_rate: '',
    pay_frequency: 'biweekly',
    country: 'US',
    state: '',
    filing_status: 'single',
    allowances: 0
  });

  useEffect(() => {
    fetchEmployees();
    fetchTaxTables();
  }, [companyId]);

  const fetchEmployees = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/payroll-full/employees${params}`);
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxTables = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/tax-tables`);
      if (res.ok) {
        const data = await res.json();
        setTaxTables(data);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const generateEmployeeId = () => {
    const prefix = 'EMP';
    const num = String(employees.length + 1).padStart(3, '0');
    return `${prefix}-${num}`;
  };

  const handleAdd = async () => {
    if (!newEmp.name || !newEmp.pay_rate) {
      alert('Name and pay rate are required');
      return;
    }

    try {
      const empData = {
        ...newEmp,
        employee_id: newEmp.employee_id || generateEmployeeId(),
        pay_rate: parseFloat(newEmp.pay_rate) || 0,
        allowances: parseInt(newEmp.allowances) || 0,
        company_id: companyId
      };

      const res = await fetch(`${API_URL}/api/payroll-full/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(empData)
      });

      if (res.ok) {
        setNewEmp({
          name: '',
          employee_id: '',
          position: '',
          hire_date: new Date().toISOString().split('T')[0],
          pay_type: 'hourly',
          pay_rate: '',
          pay_frequency: 'biweekly',
          country: 'US',
          state: '',
          filing_status: 'single',
          allowances: 0
        });
        setShowForm(false);
        fetchEmployees();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to create employee');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to create employee');
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/employees/${employeeId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchEmployees();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees Setup</h1>
          <p className="text-gray-500 mt-1">Add and manage employee profiles for payroll</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/setup')}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
          </Button>
          <Button onClick={() => setShowForm(true)} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />Add Employee
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>Add New Employee</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Full Name *</label>
                <Input 
                  value={newEmp.name}
                  onChange={(e) => setNewEmp({...newEmp, name: e.target.value})}
                  data-testid="emp-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Employee ID</label>
                <Input 
                  placeholder="Auto-generated"
                  value={newEmp.employee_id}
                  onChange={(e) => setNewEmp({...newEmp, employee_id: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Position</label>
                <Input 
                  value={newEmp.position}
                  onChange={(e) => setNewEmp({...newEmp, position: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Hire Date</label>
                <Input 
                  type="date"
                  value={newEmp.hire_date}
                  onChange={(e) => setNewEmp({...newEmp, hire_date: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Pay Type</label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={newEmp.pay_type}
                  onChange={(e) => setNewEmp({...newEmp, pay_type: e.target.value})}
                >
                  <option value="hourly">Hourly</option>
                  <option value="salary">Salary</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">
                  {newEmp.pay_type === 'hourly' ? 'Hourly Rate ($) *' : 'Annual Salary ($) *'}
                </label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newEmp.pay_rate}
                  onChange={(e) => setNewEmp({...newEmp, pay_rate: e.target.value})}
                  data-testid="emp-rate-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Pay Frequency</label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={newEmp.pay_frequency}
                  onChange={(e) => setNewEmp({...newEmp, pay_frequency: e.target.value})}
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="semimonthly">Semi-Monthly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>

            {/* Tax Location */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Tax Location</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Country</label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={newEmp.country}
                    onChange={(e) => setNewEmp({...newEmp, country: e.target.value, state: ''})}
                  >
                    <option value="US">United States</option>
                    {taxTables.west_africa.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {newEmp.country === 'US' && (
                  <div>
                    <label className="text-sm font-medium">State</label>
                    <select 
                      className="w-full border rounded-md p-2"
                      value={newEmp.state}
                      onChange={(e) => setNewEmp({...newEmp, state: e.target.value})}
                    >
                      <option value="">Select state...</option>
                      {taxTables.us_states.map(s => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium">Filing Status</label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={newEmp.filing_status}
                    onChange={(e) => setNewEmp({...newEmp, filing_status: e.target.value})}
                  >
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="married_separate">Married Filing Separately</option>
                    <option value="head_of_household">Head of Household</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={handleAdd} data-testid="save-employee-btn">Save Employee</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading employees...</div>
      ) : employees.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">No employees yet</h3>
          <p className="text-sm text-gray-500 mt-1">Add your first employee to get started with payroll</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {employees.map((emp) => (
            <Card key={emp.employee_id || emp._id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <UserCircle className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{emp.name}</h3>
                    <p className="text-sm text-gray-500">
                      {emp.employee_id} • {emp.position || 'No position'} • {emp.country === 'US' ? `US - ${emp.state || 'No state'}` : emp.country}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-semibold">
                      {emp.pay_type === 'salary' 
                        ? `$${(emp.pay_rate || 0).toLocaleString()}/yr`
                        : `$${(emp.pay_rate || 0).toFixed(2)}/hr`
                      }
                    </p>
                    <p className="text-sm text-gray-500">{emp.pay_frequency}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(emp.employee_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

// Placeholder components with working buttons
const AccountDesign = () => {
  const [saved, setSaved] = useState(false);
  return (
    <>
      <div><h1 className="text-2xl font-bold text-gray-900">Account Design</h1></div>
      {saved && <div className="p-4 bg-emerald-50 text-emerald-700 rounded-lg flex items-center gap-2"><CheckCircle className="w-5 h-5" />Settings saved!</div>}
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-600 mb-4">Configure your chart of accounts structure</p>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Account Number Length</label>
              <select className="w-full mt-1 p-2 border rounded-md">
                <option>4 digits</option>
                <option>5 digits</option>
                <option>6 digits</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Sub-account Separator</label>
              <select className="w-full mt-1 p-2 border rounded-md">
                <option>Hyphen (-)</option>
                <option>Period (.)</option>
                <option>None</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end pt-4 mt-4 border-t">
            <Button onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 3000); }}>
              <Save className="w-4 h-4 mr-2" />Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

const CostingOptions = () => {
  const [saved, setSaved] = useState(false);
  return (
    <>
      <div><h1 className="text-2xl font-bold text-gray-900">Costing Options</h1></div>
      {saved && <div className="p-4 bg-emerald-50 text-emerald-700 rounded-lg flex items-center gap-2"><CheckCircle className="w-5 h-5" />Settings saved!</div>}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium">Inventory Costing Method</label>
            <select className="w-full mt-1 p-2 border rounded-md">
              <option>FIFO (First In, First Out)</option>
              <option>LIFO (Last In, First Out)</option>
              <option>Average Cost</option>
            </select>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 3000); }}>
              <Save className="w-4 h-4 mr-2" />Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

const EnterprisesSetup = () => {
  const [enterprises, setEnterprises] = useState([
    { code: 'FARM', name: 'Farm Operations' },
    { code: 'ADMIN', name: 'Administration' },
  ]);
  const [showForm, setShowForm] = useState(false);
  const [newEnt, setNewEnt] = useState({ code: '', name: '' });

  const handleAdd = () => {
    if (!newEnt.code || !newEnt.name) return;
    setEnterprises([...enterprises, newEnt]);
    setNewEnt({ code: '', name: '' });
    setShowForm(false);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">Enterprises / Operations</h1></div>
        <Button onClick={() => setShowForm(true)}><Plus className="w-4 h-4 mr-2" />Add Enterprise</Button>
      </div>
      {showForm && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium">Code</label><Input value={newEnt.code} onChange={(e) => setNewEnt({...newEnt, code: e.target.value})} /></div>
              <div><label className="text-sm font-medium">Name</label><Input value={newEnt.name} onChange={(e) => setNewEnt({...newEnt, name: e.target.value})} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={handleAdd}>Save</Button>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-4">
        {enterprises.map((ent) => (
          <Card key={ent.code}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-lg"><Briefcase className="w-6 h-6 text-indigo-600" /></div>
              <div><h3 className="font-semibold">{ent.name}</h3><p className="text-sm text-gray-500">Code: {ent.code}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

const VendorsCustomersSetup = () => {
  const navigate = useNavigate();
  return (
    <>
      <div><h1 className="text-2xl font-bold text-gray-900">Vendors & Customers</h1></div>
      <div className="grid grid-cols-2 gap-6">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/view/vendors')}>
          <CardContent className="p-6 text-center">
            <Building className="w-12 h-12 text-orange-500 mx-auto mb-3" />
            <h3 className="font-semibold">Manage Vendors</h3>
            <p className="text-sm text-gray-500">Add and edit vendor records</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/view/customers')}>
          <CardContent className="p-6 text-center">
            <Users className="w-12 h-12 text-sky-500 mx-auto mb-3" />
            <h3 className="font-semibold">Manage Customers</h3>
            <p className="text-sm text-gray-500">Add and edit customer records</p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

const DeductionsSetup = () => {
  const { companyId } = useCompany();
  const [activeTab, setActiveTab] = useState('deductions');
  const [deductions, setDeductions] = useState([
    { id: 1, name: 'Federal Tax', type: 'Tax', calculation: 'Variable', amount: 0 },
    { id: 2, name: 'State Tax', type: 'Tax', calculation: 'Variable', amount: 0 },
    { id: 3, name: 'Social Security', type: 'Tax', calculation: '6.2%', amount: 0 },
    { id: 4, name: 'Medicare', type: 'Tax', calculation: '1.45%', amount: 0 },
    { id: 5, name: 'Health Insurance', type: 'Benefit', calculation: 'Fixed', amount: 150 },
    { id: 6, name: '401(k) Contribution', type: 'Retirement', calculation: 'Percentage', amount: 6 },
  ]);
  const [otherIncome, setOtherIncome] = useState([
    { id: 1, name: 'Commission', type: 'Earnings', calculation: 'Variable', taxable: true },
    { id: 2, name: 'Bonus', type: 'Earnings', calculation: 'Fixed', taxable: true },
    { id: 3, name: 'Overtime Premium', type: 'Earnings', calculation: '1.5x Rate', taxable: true },
    { id: 4, name: 'Travel Reimbursement', type: 'Reimbursement', calculation: 'Fixed', taxable: false },
    { id: 5, name: 'Mileage Allowance', type: 'Reimbursement', calculation: 'Per Mile', taxable: false },
  ]);
  const [showForm, setShowForm] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', type: 'Tax', calculation: 'Fixed', amount: 0, taxable: true });
  const [loading, setLoading] = useState(false);

  const handleAddDeduction = async () => {
    if (!newItem.name) return;
    setLoading(true);
    try {
      const newDed = { ...newItem, id: deductions.length + 1 };
      setDeductions([...deductions, newDed]);
      setNewItem({ name: '', type: 'Tax', calculation: 'Fixed', amount: 0, taxable: true });
      setShowForm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOtherIncome = async () => {
    if (!newItem.name) return;
    setLoading(true);
    try {
      const newInc = { ...newItem, id: otherIncome.length + 1 };
      setOtherIncome([...otherIncome, newInc]);
      setNewItem({ name: '', type: 'Earnings', calculation: 'Fixed', amount: 0, taxable: true });
      setShowForm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id, isDeduction = true) => {
    if (isDeduction) {
      setDeductions(deductions.filter(d => d.id !== id));
    } else {
      setOtherIncome(otherIncome.filter(o => o.id !== id));
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deductions & Other Income</h1>
          <p className="text-gray-500 mt-1">Configure payroll deductions and additional income types</p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />Add {activeTab === 'deductions' ? 'Deduction' : 'Other Income'}
        </Button>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveTab('deductions')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'deductions' ? 'bg-sky-100 text-sky-700 border-b-2 border-sky-600' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Deductions ({deductions.length})
        </button>
        <button
          onClick={() => setActiveTab('other-income')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'other-income' ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-600' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Other Income ({otherIncome.length})
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <Card className={activeTab === 'deductions' ? 'border-amber-200 bg-amber-50/30' : 'border-emerald-200 bg-emerald-50/30'}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>{activeTab === 'deductions' ? 'Add Deduction' : 'Add Other Income'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Name *</label>
                <Input value={newItem.name} onChange={(e) => setNewItem({...newItem, name: e.target.value})} placeholder={activeTab === 'deductions' ? 'e.g., Union Dues' : 'e.g., Referral Bonus'} />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <select className="w-full mt-1 p-2 border rounded-md" value={newItem.type} onChange={(e) => setNewItem({...newItem, type: e.target.value})}>
                  {activeTab === 'deductions' ? (
                    <>
                      <option value="Tax">Tax</option>
                      <option value="Benefit">Benefit</option>
                      <option value="Retirement">Retirement</option>
                      <option value="Garnishment">Garnishment</option>
                      <option value="Other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="Earnings">Earnings</option>
                      <option value="Bonus">Bonus</option>
                      <option value="Commission">Commission</option>
                      <option value="Reimbursement">Reimbursement</option>
                      <option value="Allowance">Allowance</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Calculation</label>
                <select className="w-full mt-1 p-2 border rounded-md" value={newItem.calculation} onChange={(e) => setNewItem({...newItem, calculation: e.target.value})}>
                  <option value="Fixed">Fixed Amount</option>
                  <option value="Percentage">Percentage</option>
                  <option value="Variable">Variable</option>
                  <option value="Per Hour">Per Hour</option>
                </select>
              </div>
              {newItem.calculation !== 'Variable' && (
                <div>
                  <label className="text-sm font-medium">Amount / Rate</label>
                  <Input type="number" step="0.01" value={newItem.amount} onChange={(e) => setNewItem({...newItem, amount: parseFloat(e.target.value) || 0})} />
                </div>
              )}
              {activeTab === 'other-income' && (
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={newItem.taxable} onChange={(e) => setNewItem({...newItem, taxable: e.target.checked})} className="w-4 h-4" />
                  <label className="text-sm font-medium">Taxable</label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={activeTab === 'deductions' ? handleAddDeduction : handleAddOtherIncome} disabled={loading}>
                {loading && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deductions Tab */}
      {activeTab === 'deductions' && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Calculation</th>
                  <th className="p-3 text-right">Amount/Rate</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {deductions.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="p-3 font-medium">{d.name}</td>
                    <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${d.type === 'Tax' ? 'bg-red-100 text-red-700' : d.type === 'Benefit' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}>{d.type}</span></td>
                    <td className="p-3 text-gray-600">{d.calculation}</td>
                    <td className="p-3 text-right font-mono">{d.calculation === 'Variable' ? '-' : d.calculation === 'Percentage' ? `${d.amount}%` : `$${d.amount}`}</td>
                    <td className="p-3 text-center"><Button variant="ghost" size="sm" onClick={() => handleDelete(d.id, true)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Other Income Tab */}
      {activeTab === 'other-income' && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Calculation</th>
                  <th className="p-3 text-center">Taxable</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {otherIncome.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50">
                    <td className="p-3 font-medium">{o.name}</td>
                    <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${o.type === 'Earnings' ? 'bg-emerald-100 text-emerald-700' : o.type === 'Reimbursement' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}`}>{o.type}</span></td>
                    <td className="p-3 text-gray-600">{o.calculation}</td>
                    <td className="p-3 text-center">{o.taxable ? <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /> : <Circle className="w-5 h-5 text-gray-300 mx-auto" />}</td>
                    <td className="p-3 text-center"><Button variant="ghost" size="sm" onClick={() => handleDelete(o.id, false)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

const TaxTablesSetup = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  
  // Tax Config State
  const [taxTables, setTaxTables] = useState({ us_states: [], west_africa: [], us_federal: {} });
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedState, setSelectedState] = useState('');
  const [payrollIndustry, setPayrollIndustry] = useState('general');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Time Entry State
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({ employee_id: '', date: new Date().toISOString().split('T')[0], regular_hours: 8, overtime_hours: 0, double_time_hours: 0 });
  const [aiProcessing, setAiProcessing] = useState(false);
  
  // Payroll Run State
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [currentRun, setCurrentRun] = useState(null);
  const [runProgress, setRunProgress] = useState([]);
  const [payPeriod, setPayPeriod] = useState({ start: '', end: '', pay_date: '' });
  
  // Register State
  const [payRegister, setPayRegister] = useState(null);
  const [selectedPaycheck, setSelectedPaycheck] = useState(null);
  
  // Reports State
  const [ytdReport, setYtdReport] = useState(null);
  const [form941, setForm941] = useState(null);
  const [formDE9, setFormDE9] = useState(null);
  const [importData, setImportData] = useState('');

  const tabs = [
    { id: 'config', label: 'Tax Configuration', icon: BadgePercent },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'time', label: 'Time Entry', icon: Clock },
    { id: 'run', label: 'Run Payroll', icon: Play },
    { id: 'review', label: 'Review & Approve', icon: Eye },
    { id: 'post', label: 'Post to GL', icon: Send },
    { id: 'reports', label: 'Reports', icon: FileText },
  ];

  useEffect(() => {
    fetchTaxTables();
    fetchCompanyConfig();
    fetchEmployees();
    fetchTimeEntries();
    fetchPayrollRuns();
  }, [companyId]);

  const fetchTaxTables = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/tax-tables`);
      if (res.ok) setTaxTables(await res.json());
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  };

  const fetchCompanyConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/system/company-config`);
      if (res.ok) {
        const data = await res.json();
        if (data.payroll_country) {
          setSelectedCountry(data.payroll_country);
          setSelectedState(data.payroll_state || '');
          setPayrollIndustry(data.payroll_industry || 'general');
        }
      }
    } catch (err) { console.error('Error:', err); }
  };

  const fetchEmployees = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/payroll-full/employees${params}`);
      if (res.ok) setEmployees(await res.json());
    } catch (err) { console.error('Error:', err); }
  };

  const fetchTimeEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/time-entries`);
      if (res.ok) setTimeEntries(await res.json());
    } catch (err) { console.error('Error:', err); }
  };

  const fetchPayrollRuns = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/payroll-runs`);
      if (res.ok) setPayrollRuns(await res.json());
    } catch (err) { console.error('Error:', err); }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/system/company-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payroll_country: selectedCountry,
          payroll_state: selectedCountry === 'US' ? selectedState : null,
          payroll_currency: selectedCountry === 'US' ? 'USD' : 'XOF',
          payroll_industry: selectedCountry === 'US' && selectedState === 'CA' ? payrollIndustry : null
        })
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 3000); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { console.error('Error:', err); }
    finally { setSaving(false); }
  };

  // AI-Powered Time Entry
  const handleAddTimeEntry = async () => {
    if (!newEntry.employee_id) { toast.error('Select an employee'); return; }
    setAiProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/time-entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEntry)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Time entry added. ${data.ai_notes?.[1] || ''}`);
        fetchTimeEntries();
        setNewEntry({ ...newEntry, employee_id: '', regular_hours: 8, overtime_hours: 0, double_time_hours: 0 });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { toast.error('Failed to add entry'); }
    finally { setAiProcessing(false); }
  };

  // AI-Powered Payroll Calculation
  const handleRunPayroll = async () => {
    if (!payPeriod.start || !payPeriod.end || !payPeriod.pay_date) {
      toast.error('Enter pay period dates');
      return;
    }
    setAiProcessing(true);
    setRunProgress([{ step: 'Initializing', status: 'running', message: 'AI preparing payroll calculation...' }]);
    
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pay_period_start: payPeriod.start,
          pay_period_end: payPeriod.end,
          pay_date: payPeriod.pay_date
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCurrentRun(data);
        setRunProgress(data.progress || []);
        toast.success(`Payroll calculated for ${data.summary?.employees || 0} employees`);
        setActiveTab('review');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Payroll calculation failed');
      }
    } catch (err) { toast.error('Failed to run payroll'); }
    finally { setAiProcessing(false); }
  };

  // Fetch Pay Register
  const handleViewRegister = async (payrollId) => {
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/payroll/${payrollId}/register`);
      if (res.ok) {
        const data = await res.json();
        setPayRegister(data.register);
      }
    } catch (err) { toast.error('Failed to load register'); }
  };

  // Fetch Individual Paycheck
  const handleViewPaycheck = async (payrollId, employeeId) => {
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/payroll/${payrollId}/paycheck/${employeeId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedPaycheck(data);
      }
    } catch (err) { toast.error('Failed to load paycheck'); }
  };

  // Post Payroll
  const handlePostPayroll = async (payrollId) => {
    if (!window.confirm('Post this payroll to employee records and GL? This action is permanent.')) return;
    setAiProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/payroll/${payrollId}/post`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setCurrentRun(null);
        fetchPayrollRuns();
        setActiveTab('config');
      } else {
        const err = await res.json();
        toast.error(err.message || 'Post failed');
      }
    } catch (err) { toast.error('Failed to post payroll'); }
    finally { setAiProcessing(false); }
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax & Payroll Setup</h1>
          <p className="text-gray-500 mt-1">Complete payroll workflow with AI automation</p>
        </div>
        <div className="flex items-center gap-3">
          {saving && <div className="flex items-center gap-2 px-3 py-1.5 bg-sky-50 text-sky-700 rounded-lg text-sm"><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></div>}
          {saved && !saving && <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium"><CheckCircle className="w-4 h-4" /><span>Saved!</span></div>}
          <Button variant="outline" onClick={() => navigate('/setup')}><ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back</Button>
        </div>
      </div>

      {/* Workflow Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
        {tabs.map((tab, idx) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden md:inline">{tab.label}</span>
            <span className="md:hidden">{idx + 1}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'config' && (
        <TaxConfigTab 
          taxTables={taxTables}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
          selectedState={selectedState}
          setSelectedState={setSelectedState}
          payrollIndustry={payrollIndustry}
          setPayrollIndustry={setPayrollIndustry}
          handleSaveConfig={handleSaveConfig}
          loading={loading}
          companyId={companyId}
        />
      )}

      {activeTab === 'time' && (
        <TimeEntryTab
          employees={employees}
          timeEntries={timeEntries}
          newEntry={newEntry}
          setNewEntry={setNewEntry}
          handleAddTimeEntry={handleAddTimeEntry}
          aiProcessing={aiProcessing}
          fetchTimeEntries={fetchTimeEntries}
        />
      )}

      {activeTab === 'run' && (
        <RunPayrollTab
          payPeriod={payPeriod}
          setPayPeriod={setPayPeriod}
          handleRunPayroll={handleRunPayroll}
          aiProcessing={aiProcessing}
          runProgress={runProgress}
          currentRun={currentRun}
          timeEntries={timeEntries}
        />
      )}

      {activeTab === 'review' && (
        <ReviewApproveTab
          currentRun={currentRun}
          payRegister={payRegister}
          handleViewRegister={handleViewRegister}
          selectedPaycheck={selectedPaycheck}
          handleViewPaycheck={handleViewPaycheck}
          setSelectedPaycheck={setSelectedPaycheck}
          payrollRuns={payrollRuns}
          setCurrentRun={setCurrentRun}
          loadPayrollRuns={fetchPayrollRuns}
        />
      )}

      {activeTab === 'post' && (
        <PostPayrollTab
          currentRun={currentRun}
          handlePostPayroll={handlePostPayroll}
          aiProcessing={aiProcessing}
          payrollRuns={payrollRuns}
          setCurrentRun={setCurrentRun}
          loadPayrollRuns={fetchPayrollRuns}
        />
      )}

      {activeTab === 'employees' && (
        <EmployeesTab
          employees={employees}
          fetchEmployees={fetchEmployees}
          companyId={companyId}
        />
      )}

      {activeTab === 'reports' && (
        <ReportsTab companyId={companyId} />
      )}
    </>
  );
};

// Sub-components for each tab
const TaxConfigTab = ({ taxTables, selectedCountry, setSelectedCountry, selectedState, setSelectedState, payrollIndustry, setPayrollIndustry, handleSaveConfig, loading, companyId }) => {
  const [viewMode, setViewMode] = useState('tables'); // 'tables' or 'region'
  
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>;
  
  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex gap-2 mb-4">
        <Button 
          variant={viewMode === 'tables' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setViewMode('tables')}
        >
          <BadgePercent className="w-4 h-4 mr-2" /> Tax Tables
        </Button>
        <Button 
          variant={viewMode === 'region' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setViewMode('region')}
        >
          <Settings2 className="w-4 h-4 mr-2" /> Region Settings
        </Button>
      </div>

      {viewMode === 'tables' ? (
        <TaxTableManager companyId={companyId} />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Payroll Region</CardTitle>
              <CardDescription>Select where your employees are located</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => { setSelectedCountry('US'); setSelectedState(''); }}
                  className={`p-4 border rounded-lg text-left transition-all ${selectedCountry === 'US' ? 'border-sky-500 bg-sky-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🇺🇸</span>
                    <div><p className="font-semibold">United States</p><p className="text-sm text-gray-500">51 states including DC</p></div>
                  </div>
                </button>
                <button
                  onClick={() => { setSelectedCountry(''); setSelectedState(''); }}
                  className={`p-4 border rounded-lg text-left transition-all ${selectedCountry !== 'US' && selectedCountry ? 'border-amber-500 bg-amber-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🌍</span>
                    <div><p className="font-semibold">West Africa</p><p className="text-sm text-gray-500">16 countries supported</p></div>
                  </div>
                </button>
              </div>

              {selectedCountry === 'US' && (
                <div className="space-y-2">
                  <label className="font-medium">Select State</label>
                  <select className="w-full border rounded-md p-2" value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
                    <option value="">Select a state...</option>
                    {taxTables.us_states?.map(state => (
                      <option key={state.code} value={state.code}>{state.name} ({state.type === 'none' ? 'No State Tax' : `${(state.rate * 100).toFixed(2)}%`})</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedCountry === 'US' && selectedState === 'CA' && (
                <div className="space-y-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Tractor className="w-5 h-5 text-amber-600" />
                    <label className="font-medium text-amber-800">California Payroll Industry Type</label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {['farm', 'construction', 'general', 'other'].map(type => (
                      <label key={type} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${payrollIndustry === type ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input type="radio" name="payroll_industry" value={type} checked={payrollIndustry === type} onChange={(e) => setPayrollIndustry(e.target.value)} className="w-4 h-4" />
                        <span className="font-medium capitalize">{type === 'farm' ? 'Farm / Agriculture' : type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSaveConfig}><Save className="w-4 h-4 mr-2" /> Save Configuration</Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const TimeEntryTab = ({ employees, timeEntries, newEntry, setNewEntry, handleAddTimeEntry, aiProcessing, fetchTimeEntries }) => (
  <div className="space-y-4">
    {/* AI Assistant */}
    <Card className="bg-gradient-to-r from-purple-50 to-sky-50 border-purple-200">
      <CardContent className="p-4 flex items-center gap-4">
        <Brain className="w-10 h-10 text-purple-500" />
        <div className="flex-1">
          <h3 className="font-semibold text-purple-900">AI Time Entry Assistant</h3>
          <p className="text-sm text-purple-700">Automatically calculates overtime based on CA Agriculture rules (10 hrs/day, 60 hrs/week). Double-time after 12 hours.</p>
        </div>
      </CardContent>
    </Card>

    {/* Add Entry Form */}
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Add Time Entry</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="col-span-2">
            <label className="text-sm font-medium">Employee</label>
            <select className="w-full border rounded-md p-2" value={newEntry.employee_id} onChange={(e) => setNewEntry({...newEntry, employee_id: e.target.value})}>
              <option value="">Select employee...</option>
              {employees.map(emp => (
                <option key={emp.employee_id} value={emp.employee_id}>{emp.name || `${emp.first_name} ${emp.last_name}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Date</label>
            <Input type="date" value={newEntry.date} onChange={(e) => setNewEntry({...newEntry, date: e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-medium">Regular Hrs</label>
            <Input type="number" value={newEntry.regular_hours} onChange={(e) => setNewEntry({...newEntry, regular_hours: parseFloat(e.target.value) || 0})} />
          </div>
          <div>
            <label className="text-sm font-medium">OT Hrs</label>
            <Input type="number" value={newEntry.overtime_hours} onChange={(e) => setNewEntry({...newEntry, overtime_hours: parseFloat(e.target.value) || 0})} />
          </div>
          <div className="flex items-end">
            <Button onClick={handleAddTimeEntry} disabled={aiProcessing} className="w-full">
              {aiProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Add</>}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Time Entries List */}
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Pending Time Entries ({timeEntries.length})</CardTitle>
          <Button variant="outline" size="sm" onClick={fetchTimeEntries}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        {timeEntries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No pending time entries. Add entries above.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2 text-sm">Employee</th>
                <th className="text-left p-2 text-sm">Date</th>
                <th className="text-right p-2 text-sm">Regular</th>
                <th className="text-right p-2 text-sm">OT</th>
                <th className="text-right p-2 text-sm">DT</th>
                <th className="text-right p-2 text-sm">Total</th>
              </tr>
            </thead>
            <tbody>
              {timeEntries.slice(0, 20).map((entry, idx) => (
                <tr key={idx} className="border-b">
                  <td className="p-2 text-sm">{entry.employee_name || entry.employee_id}</td>
                  <td className="p-2 text-sm">{entry.date}</td>
                  <td className="p-2 text-sm text-right">{entry.calculated_regular || entry.regular_hours}</td>
                  <td className="p-2 text-sm text-right text-amber-600">{entry.calculated_ot || entry.overtime_hours}</td>
                  <td className="p-2 text-sm text-right text-red-600">{entry.calculated_dt || entry.double_time_hours}</td>
                  <td className="p-2 text-sm text-right font-medium">{(entry.calculated_regular || entry.regular_hours) + (entry.calculated_ot || entry.overtime_hours) + (entry.calculated_dt || entry.double_time_hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  </div>
);

const RunPayrollTab = ({ payPeriod, setPayPeriod, handleRunPayroll, aiProcessing, runProgress, currentRun, timeEntries }) => {
  // Quick select pay period options
  const setQuickPeriod = (type) => {
    const today = new Date();
    let start, end, payDate;
    
    if (type === 'current-biweekly') {
      // Current biweekly period
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Start Monday
      start = new Date(today);
      start.setDate(today.getDate() - diff - 7); // Start of last week
      end = new Date(start);
      end.setDate(start.getDate() + 13); // 2 weeks
      payDate = new Date(end);
      payDate.setDate(end.getDate() + 5); // Pay 5 days after period end
    } else if (type === 'last-biweekly') {
      // Previous biweekly period
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      start = new Date(today);
      start.setDate(today.getDate() - diff - 21); // Start 3 weeks ago
      end = new Date(start);
      end.setDate(start.getDate() + 13);
      payDate = new Date(end);
      payDate.setDate(end.getDate() + 5);
    } else if (type === 'current-month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      payDate = new Date(today.getFullYear(), today.getMonth() + 1, 5);
    }
    
    setPayPeriod({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      pay_date: payDate.toISOString().split('T')[0]
    });
  };

  return (
    <div className="space-y-4">
      {/* AI Processing Card */}
      <Card className="bg-gradient-to-r from-sky-50 to-emerald-50 border-sky-200">
        <CardContent className="p-4 flex items-center gap-4">
          <Calculator className="w-10 h-10 text-sky-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-sky-900">AI Payroll Engine</h3>
            <p className="text-sm text-sky-700">Calculates gross pay, overtime, all taxes, deductions, and net pay with full audit trail.</p>
          </div>
        </CardContent>
      </Card>

      {/* Pay Period Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Pay Period</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Quick Select Buttons */}
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-500 block mb-2">Quick Select:</label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setQuickPeriod('current-biweekly')}>
                Current Biweekly
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickPeriod('last-biweekly')}>
                Previous Biweekly
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickPeriod('current-month')}>
                Current Month
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Period Start</label>
              <Input 
                type="date" 
                value={payPeriod.start} 
                onChange={(e) => setPayPeriod({...payPeriod, start: e.target.value})}
                data-testid="pay-period-start"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Period End</label>
              <Input 
                type="date" 
                value={payPeriod.end} 
                onChange={(e) => setPayPeriod({...payPeriod, end: e.target.value})}
                data-testid="pay-period-end"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Pay Date</label>
              <Input 
                type="date" 
                value={payPeriod.pay_date} 
                onChange={(e) => setPayPeriod({...payPeriod, pay_date: e.target.value})}
                data-testid="pay-date"
              />
            </div>
          </div>

          {payPeriod.start && payPeriod.end && (
            <div className="mt-3 p-3 bg-sky-50 rounded-lg border border-sky-100">
              <p className="text-sm text-sky-800">
                <strong>Selected Period:</strong> {new Date(payPeriod.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(payPeriod.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {payPeriod.pay_date && <span className="ml-2">| <strong>Pay Date:</strong> {new Date(payPeriod.pay_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
              </p>
            </div>
          )}

          <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center justify-between">
            <p className="text-sm"><strong>Pending Time Entries:</strong> {timeEntries.length} entries ready for processing</p>
            {timeEntries.length > 0 && (
              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">Ready</span>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleRunPayroll} 
              disabled={aiProcessing || timeEntries.length === 0 || !payPeriod.start || !payPeriod.end} 
              className="bg-emerald-600 hover:bg-emerald-700" 
              data-testid="run-payroll-btn"
            >
              {aiProcessing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating...</> : <><Play className="w-4 h-4 mr-2" /> Run Payroll</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Progress Steps */}
      {runProgress.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5" /> Calculation Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {runProgress.map((step, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  {step.status === 'complete' ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : step.status === 'running' ? (
                  <Loader2 className="w-5 h-5 text-sky-500 animate-spin" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-300" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-sm">{step.step}</p>
                  <p className="text-xs text-gray-500">{step.message}</p>
                </div>
                {step.progress && <span className="text-sm text-emerald-600">{step.progress}%</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    {/* Summary */}
    {currentRun && (
      <Card className="bg-emerald-50 border-emerald-200">
        <CardHeader>
          <CardTitle className="text-emerald-800">✓ Payroll Calculated</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{currentRun.summary?.employees || 0}</p>
              <p className="text-xs text-gray-500">Employees</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-gray-900">${(currentRun.summary?.total_gross || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Gross</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-red-600">${(currentRun.summary?.total_deductions || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">Deductions</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">${(currentRun.summary?.total_net || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">Net Pay</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-white rounded-lg">
            <h4 className="font-medium mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-500" /> AI Notes</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              {currentRun.ai_notes?.map((note, idx) => <li key={idx}>{note}</li>)}
            </ul>
          </div>
        </CardContent>
      </Card>
    )}
  </div>
  );
};

const ReviewApproveTab = ({ currentRun, payRegister, handleViewRegister, selectedPaycheck, handleViewPaycheck, setSelectedPaycheck, payrollRuns, setCurrentRun, loadPayrollRuns }) => {
  // If no currentRun but we have runs, auto-load the most recent one
  React.useEffect(() => {
    if (!currentRun && payrollRuns?.length > 0) {
      setCurrentRun(payrollRuns[0]);
    }
  }, [currentRun, payrollRuns, setCurrentRun]);

  return (
    <div className="space-y-4">
      {/* Payroll Run Selector */}
      {payrollRuns?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">Select Payroll Run:</label>
              <select 
                className="border rounded-md p-2 flex-1 max-w-xs"
                value={currentRun?.payroll_id || ''}
                onChange={(e) => {
                  const run = payrollRuns.find(r => r.payroll_id === e.target.value);
                  if (run) setCurrentRun(run);
                }}
              >
                {payrollRuns.map(run => (
                  <option key={run.payroll_id} value={run.payroll_id}>
                    {run.payroll_id} - {run.pay_period_start} to {run.pay_period_end} ({run.status})
                  </option>
                ))}
              </select>
              <Button variant="outline" size="sm" onClick={loadPayrollRuns}>
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!currentRun && (!payrollRuns || payrollRuns.length === 0) ? (
        <Card className="text-center py-12">
          <Eye className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">No Payroll to Review</h3>
          <p className="text-sm text-gray-500 mt-1">Run payroll first to see results here</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={loadPayrollRuns}>
            <RefreshCw className="w-4 h-4 mr-1" /> Check for Payroll Runs
          </Button>
        </Card>
      ) : currentRun && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-sky-600">{currentRun.summary?.employees || currentRun.calculations?.length || 0}</p>
                <p className="text-xs text-gray-500">Employees</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">${(currentRun.totals?.total_gross || currentRun.summary?.total_gross || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Total Gross</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">${(currentRun.totals?.total_employee_taxes || currentRun.summary?.total_deductions || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Total Deductions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">${(currentRun.totals?.total_net || currentRun.summary?.total_net || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Net Pay</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  currentRun.status === 'posted' ? 'bg-emerald-100 text-emerald-700' :
                  currentRun.status === 'calculated' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {currentRun.status?.toUpperCase() || 'PENDING'}
                </span>
                <p className="text-xs text-gray-500 mt-1">Status</p>
              </CardContent>
            </Card>
          </div>

          {/* Pay Register */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5" /> Pay Register</CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleViewRegister(currentRun.payroll_id)}>
                  <FileText className="w-4 h-4 mr-1" /> Load Register
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {payRegister ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-2">Employee</th>
                          <th className="text-right p-2">Hours</th>
                          <th className="text-right p-2">Gross</th>
                          <th className="text-right p-2">Federal</th>
                          <th className="text-right p-2">State</th>
                          <th className="text-right p-2">SS</th>
                          <th className="text-right p-2">Med</th>
                          <th className="text-right p-2">SDI</th>
                          <th className="text-right p-2">Net</th>
                          <th className="text-center p-2">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payRegister.register_lines?.map((line, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2">{line.employee_name}</td>
                            <td className="p-2 text-right">{line.hours}</td>
                            <td className="p-2 text-right">${line.gross_pay?.toFixed(2)}</td>
                            <td className="p-2 text-right text-red-600">${line.federal?.toFixed(2)}</td>
                            <td className="p-2 text-right text-red-600">${line.state?.toFixed(2)}</td>
                            <td className="p-2 text-right text-red-600">${line.ss?.toFixed(2)}</td>
                            <td className="p-2 text-right text-red-600">${line.medicare?.toFixed(2)}</td>
                            <td className="p-2 text-right text-red-600">${line.sdi?.toFixed(2)}</td>
                            <td className="p-2 text-right font-medium text-emerald-600">${line.net_pay?.toFixed(2)}</td>
                            <td className="p-2 text-center">
                              <button onClick={() => handleViewPaycheck(currentRun.payroll_id, line.employee_id)} className="text-sky-600 hover:text-sky-800">
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-100 font-medium">
                        <tr>
                          <td className="p-2">TOTALS</td>
                          <td className="p-2 text-right">{payRegister.totals?.total_hours}</td>
                          <td className="p-2 text-right">${payRegister.totals?.total_gross?.toFixed(2)}</td>
                          <td className="p-2 text-right text-red-600">${payRegister.totals?.total_federal?.toFixed(2)}</td>
                          <td className="p-2 text-right text-red-600">${payRegister.totals?.total_state?.toFixed(2)}</td>
                          <td className="p-2 text-right text-red-600">${payRegister.totals?.total_ss?.toFixed(2)}</td>
                          <td className="p-2 text-right text-red-600">${payRegister.totals?.total_medicare?.toFixed(2)}</td>
                          <td className="p-2 text-right text-red-600">${payRegister.totals?.total_sdi?.toFixed(2)}</td>
                          <td className="p-2 text-right text-emerald-600">${payRegister.totals?.total_net?.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline"><Printer className="w-4 h-4 mr-2" /> Print Register</Button>
                    <Button variant="outline"><Download className="w-4 h-4 mr-2" /> Export PDF</Button>
                  </div>
                </>
              ) : (
                <p className="text-center py-8 text-gray-500">Click "Load Register" to view the pay register</p>
              )}
            </CardContent>
          </Card>

        {/* Individual Paycheck Modal */}
        {selectedPaycheck && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Pay Stub - {selectedPaycheck.paycheck?.employee?.name}</CardTitle>
                <button onClick={() => setSelectedPaycheck(null)}><X className="w-5 h-5" /></button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm"><strong>Pay Period:</strong> {selectedPaycheck.paycheck?.pay_period}</p>
                  <p className="text-sm"><strong>Pay Date:</strong> {selectedPaycheck.paycheck?.pay_date}</p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Earnings</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Regular ({selectedPaycheck.paycheck?.earnings?.regular_hours} hrs)</span>
                      <span>${selectedPaycheck.paycheck?.earnings?.regular_pay?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Overtime ({selectedPaycheck.paycheck?.earnings?.overtime_hours} hrs)</span>
                      <span>${selectedPaycheck.paycheck?.earnings?.overtime_pay?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-emerald-50 rounded col-span-2 font-medium">
                      <span>Gross Pay</span>
                      <span>${selectedPaycheck.paycheck?.earnings?.gross_pay?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Deductions</h4>
                  <div className="space-y-1 text-sm">
                    {Object.entries(selectedPaycheck.paycheck?.deductions || {}).map(([key, val]) => (
                      <div key={key} className="flex justify-between p-2 bg-red-50 rounded">
                        <span>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        <span className="text-red-600">-${val?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-emerald-100 rounded-lg text-center">
                  <p className="text-sm text-emerald-700">Net Pay</p>
                  <p className="text-3xl font-bold text-emerald-800">${selectedPaycheck.paycheck?.net_pay?.toFixed(2)}</p>
                </div>

                <div className="p-3 bg-purple-50 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-500" /> AI Explanation</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {Object.entries(selectedPaycheck.ai_explanation || {}).map(([key, val]) => (
                      <li key={key}><strong>{key}:</strong> {val}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </>
    )}
  </div>
  );
};

const PostPayrollTab = ({ currentRun, handlePostPayroll, aiProcessing, payrollRuns, setCurrentRun, loadPayrollRuns }) => {
  // Auto-load latest unposted run if no currentRun
  React.useEffect(() => {
    if (!currentRun && payrollRuns?.length > 0) {
      const unpostedRun = payrollRuns.find(r => r.status === 'calculated');
      if (unpostedRun) setCurrentRun(unpostedRun);
    }
  }, [currentRun, payrollRuns, setCurrentRun]);

  // Filter to only show calculated (unposted) runs
  const unpostedRuns = payrollRuns?.filter(r => r.status === 'calculated') || [];

  return (
  <div className="space-y-4">
    {/* Payroll Run Selector for unposted runs */}
    {unpostedRuns.length > 0 && (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Select Payroll to Post:</label>
            <select 
              className="border rounded-md p-2 flex-1 max-w-xs"
              value={currentRun?.payroll_id || ''}
              onChange={(e) => {
                const run = payrollRuns.find(r => r.payroll_id === e.target.value);
                if (run) setCurrentRun(run);
              }}
            >
              {unpostedRuns.map(run => (
                <option key={run.payroll_id} value={run.payroll_id}>
                  {run.payroll_id} - {run.pay_period_start} to {run.pay_period_end}
                </option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={loadPayrollRuns}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    )}

    {!currentRun && unpostedRuns.length === 0 ? (
      <Card className="text-center py-12">
        <Send className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="font-medium text-gray-600">No Payroll to Post</h3>
        <p className="text-sm text-gray-500 mt-1">Run and review payroll first</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={loadPayrollRuns}>
          <RefreshCw className="w-4 h-4 mr-1" /> Check for Payroll Runs
        </Button>
      </Card>
    ) : currentRun?.status === 'posted' ? (
      <Card className="text-center py-12 bg-emerald-50 border-emerald-200">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h3 className="font-medium text-emerald-800">Payroll Already Posted</h3>
        <p className="text-sm text-emerald-600 mt-1">This payroll has been posted to employee records and GL</p>
      </Card>
    ) : currentRun && (
      <>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">Final Step: Post Payroll</h3>
              <p className="text-sm text-amber-700">This will permanently update employee YTD records and create GL journal entries. This action cannot be undone without a reversal entry.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pre-Post Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Pay register reviewed and verified</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Individual paychecks validated</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Tax calculations confirmed accurate</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>GL Entries to be Created</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2">Account</th>
                  <th className="text-right p-2">Debit</th>
                  <th className="text-right p-2">Credit</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b"><td className="p-2">6100 - Wage Expense</td><td className="p-2 text-right">${(currentRun.totals?.total_gross || currentRun.summary?.total_gross || 0).toFixed(2)}</td><td className="p-2"></td></tr>
                <tr className="border-b"><td className="p-2">6110 - Payroll Tax Expense</td><td className="p-2 text-right">calculated</td><td className="p-2"></td></tr>
                <tr className="border-b"><td className="p-2">2100 - Tax Payables</td><td className="p-2"></td><td className="p-2 text-right">${(currentRun.totals?.total_employee_taxes || currentRun.summary?.total_deductions || 0).toFixed(2)}</td></tr>
                <tr className="border-b"><td className="p-2">1000 - Cash</td><td className="p-2"></td><td className="p-2 text-right">${(currentRun.totals?.total_net || currentRun.summary?.total_net || 0).toFixed(2)}</td></tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={() => handlePostPayroll(currentRun.payroll_id)} disabled={aiProcessing} className="bg-emerald-600 hover:bg-emerald-700">
            {aiProcessing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting...</> : <><Send className="w-4 h-4 mr-2" /> Post Payroll to GL</>}
          </Button>
        </div>
      </>
    )}
  </div>
  );
};

// ==================== EMPLOYEES TAB ====================
const EmployeesTab = ({ employees, fetchEmployees, companyId }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newEmp, setNewEmp] = useState({
    name: '', employee_id: '', position: '', pay_type: 'hourly', pay_rate: '',
    pay_frequency: 'biweekly', state: 'CA', filing_status: 'single', allowances: 0
  });

  const handleAddEmployee = async () => {
    if (!newEmp.name || !newEmp.pay_rate) { toast.error('Name and pay rate required'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newEmp, pay_rate: parseFloat(newEmp.pay_rate), company_id: companyId })
      });
      if (res.ok) {
        toast.success('Employee added');
        setShowAddModal(false);
        fetchEmployees();
        setNewEmp({ name: '', employee_id: '', position: '', pay_type: 'hourly', pay_rate: '', pay_frequency: 'biweekly', state: 'CA', filing_status: 'single', allowances: 0 });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { toast.error('Failed to add employee'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (empId) => {
    if (!window.confirm('Delete this employee?')) return;
    try {
      await fetch(`${API_URL}/api/payroll-full/employees/${empId}`, { method: 'DELETE' });
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (err) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Employees ({employees.length})</CardTitle>
            <Button onClick={() => setShowAddModal(true)}><Plus className="w-4 h-4 mr-1" /> Add Employee</Button>
          </div>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div className="text-center py-8"><Users className="w-12 h-12 mx-auto text-gray-300 mb-2" /><p className="text-gray-500">No employees yet</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">ID</th>
                  <th className="text-left p-2">Position</th>
                  <th className="text-left p-2">Pay Type</th>
                  <th className="text-right p-2">Pay Rate</th>
                  <th className="text-left p-2">State</th>
                  <th className="text-center p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.employee_id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{emp.name || `${emp.first_name} ${emp.last_name}`}</td>
                    <td className="p-2 font-mono text-xs">{emp.employee_id}</td>
                    <td className="p-2">{emp.position || '-'}</td>
                    <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${emp.pay_type === 'salary' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>{emp.pay_type}</span></td>
                    <td className="p-2 text-right">${emp.pay_type === 'salary' ? `${(emp.pay_rate || 0).toLocaleString()}/yr` : `${emp.pay_rate}/hr`}</td>
                    <td className="p-2">{emp.state || '-'}</td>
                    <td className="p-2 text-center"><button onClick={() => handleDelete(emp.employee_id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader><CardTitle>Add Employee</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium">Name *</label><Input value={newEmp.name} onChange={(e) => setNewEmp({...newEmp, name: e.target.value})} /></div>
                <div><label className="text-sm font-medium">Employee ID</label><Input value={newEmp.employee_id} onChange={(e) => setNewEmp({...newEmp, employee_id: e.target.value})} placeholder="Auto-generated if blank" /></div>
                <div><label className="text-sm font-medium">Position</label><Input value={newEmp.position} onChange={(e) => setNewEmp({...newEmp, position: e.target.value})} /></div>
                <div><label className="text-sm font-medium">Pay Type</label>
                  <select className="w-full border rounded-md p-2" value={newEmp.pay_type} onChange={(e) => setNewEmp({...newEmp, pay_type: e.target.value})}>
                    <option value="hourly">Hourly</option><option value="salary">Salary (Annual)</option>
                  </select>
                </div>
                <div><label className="text-sm font-medium">Pay Rate *</label><Input type="number" value={newEmp.pay_rate} onChange={(e) => setNewEmp({...newEmp, pay_rate: e.target.value})} placeholder={newEmp.pay_type === 'salary' ? 'Annual salary' : 'Hourly rate'} /></div>
                <div><label className="text-sm font-medium">Pay Frequency</label>
                  <select className="w-full border rounded-md p-2" value={newEmp.pay_frequency} onChange={(e) => setNewEmp({...newEmp, pay_frequency: e.target.value})}>
                    <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="semimonthly">Semi-monthly</option><option value="monthly">Monthly</option>
                  </select>
                </div>
                <div><label className="text-sm font-medium">State</label>
                  <select className="w-full border rounded-md p-2" value={newEmp.state} onChange={(e) => setNewEmp({...newEmp, state: e.target.value})}>
                    <option value="CA">California</option><option value="TX">Texas</option><option value="NY">New York</option><option value="FL">Florida</option>
                  </select>
                </div>
                <div><label className="text-sm font-medium">Filing Status</label>
                  <select className="w-full border rounded-md p-2" value={newEmp.filing_status} onChange={(e) => setNewEmp({...newEmp, filing_status: e.target.value})}>
                    <option value="single">Single</option><option value="married">Married</option><option value="head_of_household">Head of Household</option>
                  </select>
                </div>
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddEmployee} disabled={loading}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Employee'}</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== REPORTS TAB ====================
const ReportsTab = ({ companyId }) => {
  const [activeReport, setActiveReport] = useState('ytd');
  const [ytdData, setYtdData] = useState(null);
  const [form941Data, setForm941Data] = useState(null);
  const [formDE9Data, setFormDE9Data] = useState(null);
  const [w2Data, setW2Data] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [importText, setImportText] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [w2Year, setW2Year] = useState(new Date().getFullYear() - 1);

  const fetchYTD = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/ytd-all?year=${year}${companyId ? `&company_id=${companyId}` : ''}`);
      if (res.ok) setYtdData(await res.json());
    } catch (err) { toast.error('Failed to load YTD'); }
    finally { setLoading(false); }
  };

  const fetch941 = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/form-941?year=${year}&quarter=${quarter}&company_id=${encodeURIComponent(companyId || 'default')}`);
      if (res.ok) setForm941Data(await res.json());
    } catch (err) { toast.error('Failed to load 941'); }
    finally { setLoading(false); }
  };

  const fetchDE9 = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/form-de9?year=${year}&quarter=${quarter}&company_id=${encodeURIComponent(companyId || 'default')}`);
      if (res.ok) setFormDE9Data(await res.json());
    } catch (err) { toast.error('Failed to load DE-9'); }
    finally { setLoading(false); }
  };

  const fetchW2List = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/w2-list?year=${w2Year}${companyId ? `&company_id=${companyId}` : ''}`);
      if (res.ok) setW2Data(await res.json());
    } catch (err) { toast.error('Failed to load W-2 data'); }
    finally { setLoading(false); }
  };

  const downloadW2 = (employeeId, employeeName) => {
    const safeName = employeeName.replace(/ /g, '_');
    window.open(`${API_URL}/api/reports/w2-pdf/${employeeId}?year=${w2Year}`, '_blank');
  };

  const downloadAllW2s = () => {
    window.open(`${API_URL}/api/reports/w2-bulk-pdf?year=${w2Year}${companyId ? `&company_id=${companyId}` : ''}`, '_blank');
  };

  const handleImport = async () => {
    if (!importText.trim()) { toast.error('Paste CSV data first'); return; }
    setLoading(true);
    try {
      const lines = importText.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/ /g, '_'));
      const data = lines.slice(1).map(line => {
        const vals = line.split(',');
        const obj = {};
        headers.forEach((h, i) => obj[h] = vals[i]?.trim());
        return obj;
      });
      
      const res = await fetch(`${API_URL}/api/reports/import-timesheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      });
      if (res.ok) {
        const result = await res.json();
        setImportResult(result);
        toast.success(result.message);
        if (result.success) setImportText('');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { toast.error('Import failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      {/* Report Type Selector */}
      <div className="flex gap-2 flex-wrap">
        {[
          {id: 'ytd', label: 'YTD Report'}, 
          {id: 'w2', label: 'W-2 Forms'}, 
          {id: '941', label: 'Form 941'}, 
          {id: 'de9', label: 'Form DE-9'}, 
          {id: 'import', label: 'Import Timesheet'}
        ].map(r => (
          <Button key={r.id} variant={activeReport === r.id ? 'default' : 'outline'} size="sm" onClick={() => setActiveReport(r.id)}>{r.label}</Button>
        ))}
      </div>

      {/* YTD Report */}
      {activeReport === 'ytd' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Year-To-Date Employee Report</CardTitle>
              <div className="flex items-center gap-2">
                <select className="border rounded p-1 text-sm" value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                  {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Button size="sm" onClick={fetchYTD} disabled={loading}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load'}</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!ytdData ? (
              <p className="text-center py-8 text-gray-500">Click "Load" to generate YTD report</p>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2">Employee</th>
                      <th className="text-right p-2">YTD Gross</th>
                      <th className="text-right p-2">Federal Tax</th>
                      <th className="text-right p-2">State Tax</th>
                      <th className="text-right p-2">FICA</th>
                      <th className="text-right p-2">YTD Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ytdData.employees?.map(emp => (
                      <tr key={emp.employee_id} className="border-b">
                        <td className="p-2">{emp.name}</td>
                        <td className="p-2 text-right">${emp.ytd_gross.toLocaleString()}</td>
                        <td className="p-2 text-right text-red-600">${emp.ytd_federal.toLocaleString()}</td>
                        <td className="p-2 text-right text-red-600">${emp.ytd_state.toLocaleString()}</td>
                        <td className="p-2 text-right text-red-600">${emp.ytd_fica.toLocaleString()}</td>
                        <td className="p-2 text-right font-medium text-emerald-600">${emp.ytd_net.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-medium">
                    <tr>
                      <td className="p-2">TOTALS</td>
                      <td className="p-2 text-right">${ytdData.totals?.total_gross?.toLocaleString()}</td>
                      <td className="p-2 text-right text-red-600">${ytdData.totals?.total_federal?.toLocaleString()}</td>
                      <td className="p-2 text-right text-red-600">${ytdData.totals?.total_state?.toLocaleString()}</td>
                      <td className="p-2 text-right text-red-600">${ytdData.totals?.total_fica?.toLocaleString()}</td>
                      <td className="p-2 text-right text-emerald-600">${ytdData.totals?.total_net?.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
                  <Button variant="outline" size="sm"><Printer className="w-4 h-4 mr-1" /> Print</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* W-2 Forms */}
      {activeReport === 'w2' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" /> W-2 Wage and Tax Statements
                </CardTitle>
                <CardDescription>Generate and download W-2 forms for employees</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <select className="border rounded p-1 text-sm" value={w2Year} onChange={(e) => setW2Year(parseInt(e.target.value))}>
                  {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Button size="sm" onClick={fetchW2List} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Employees'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!w2Data ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-2">Select a tax year and click "Load Employees"</p>
                <p className="text-sm text-gray-400">W-2 forms are typically generated for the previous tax year</p>
              </div>
            ) : w2Data.employees?.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-400" />
                <p className="text-gray-600 font-medium">No employees with wages found for {w2Year}</p>
                <p className="text-sm text-gray-400 mt-2">Run payroll and post to GL to generate YTD data</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-3 bg-sky-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-sky-700">{w2Data.total_employees}</p>
                    <p className="text-xs text-gray-500">Employees</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-gray-700">${(w2Data.totals?.total_wages || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Total Wages</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">${(w2Data.totals?.total_federal_tax || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Federal Tax</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">${(w2Data.totals?.total_state_tax || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">State Tax</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={downloadAllW2s}>
                    <Download className="w-4 h-4 mr-2" /> Download All W-2s (PDF)
                  </Button>
                  <Button variant="outline" onClick={() => window.open(`${API_URL}/api/reports/w2-4up-pdf?year=${w2Year}&company_id=${encodeURIComponent(companyId || 'default')}`, '_blank')}>
                    <Printer className="w-4 h-4 mr-2" /> Download W-2 4UP (Print)
                  </Button>
                  <Button variant="outline" onClick={() => window.open(`${API_URL}/api/reports/efw2-file?year=${w2Year}`, '_blank')}>
                    <FileText className="w-4 h-4 mr-2" /> Download EFW2 (Electronic)
                  </Button>
                </div>

                {/* Employee W-2 List */}
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3">Employee</th>
                      <th className="text-left p-3">SSN</th>
                      <th className="text-right p-3">Wages (Box 1)</th>
                      <th className="text-right p-3">Fed Tax (Box 2)</th>
                      <th className="text-right p-3">SS Tax (Box 4)</th>
                      <th className="text-right p-3">Medicare (Box 6)</th>
                      <th className="text-right p-3">State Tax (Box 17)</th>
                      <th className="text-center p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {w2Data.employees?.map(emp => (
                      <tr key={emp.employee_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{emp.name}</td>
                        <td className="p-3 text-gray-500 font-mono text-xs">{emp.ssn_masked}</td>
                        <td className="p-3 text-right">${emp.wages.toLocaleString()}</td>
                        <td className="p-3 text-right text-red-600">${emp.federal_tax.toLocaleString()}</td>
                        <td className="p-3 text-right">${emp.ss_tax.toLocaleString()}</td>
                        <td className="p-3 text-right">${emp.medicare_tax.toLocaleString()}</td>
                        <td className="p-3 text-right text-amber-600">${emp.state_tax.toLocaleString()}</td>
                        <td className="p-3 text-center">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => downloadW2(emp.employee_id, emp.name)}
                            data-testid={`download-w2-${emp.employee_id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> W-2
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* SSN Validation Note */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> SSN Validation for Electronic Filing
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• SSN <strong>cannot</strong> have 666 as prefix - SSA will reject the file</li>
                    <li>• SSN <strong>cannot</strong> start with 9 - Reserved for ITINs</li>
                    <li>• Use <strong>000-00-0000</strong> if SSN is unknown (employee won't receive SS credit)</li>
                    <li>• EIN must be <strong>9 digits without dashes</strong> for electronic filing</li>
                  </ul>
                </div>

                {/* Instructions */}
                <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg">
                  <h4 className="font-medium text-sky-800 mb-2">W-2 Distribution Instructions</h4>
                  <ul className="text-sm text-sky-700 space-y-1">
                    <li>• <strong>Copy B:</strong> To be filed with employee's federal tax return</li>
                    <li>• <strong>Copy C:</strong> For employee's records</li>
                    <li>• <strong>Copy 2:</strong> To be filed with employee's state/local tax return</li>
                    <li>• W-2s must be provided to employees by <strong>January 31</strong></li>
                    <li>• <strong>EFW2:</strong> Electronic file for SSA submission (Business Services Online)</li>
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Form 941 */}
      {activeReport === '941' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Form 941 - Employer's Quarterly Federal Tax Return</CardTitle>
              <div className="flex items-center gap-2">
                <select className="border rounded p-1 text-sm" value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                  {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select className="border rounded p-1 text-sm" value={quarter} onChange={(e) => setQuarter(parseInt(e.target.value))}>
                  {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
                </select>
                <Button size="sm" onClick={fetch941} disabled={loading}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!form941Data ? (
              <p className="text-center py-8 text-gray-500">Select year and quarter, then click "Generate"</p>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p><strong>Employer:</strong> {form941Data.employer?.name}</p>
                  <p><strong>EIN:</strong> {form941Data.employer?.ein}</p>
                  <p><strong>Quarter:</strong> Q{form941Data.quarter} {form941Data.year}</p>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b"><td className="p-2 w-16 font-medium">Line 1</td><td className="p-2">Number of employees</td><td className="p-2 text-right font-medium">{form941Data.line_items?.line_1}</td></tr>
                    <tr className="border-b"><td className="p-2 font-medium">Line 2</td><td className="p-2">Wages, tips, compensation</td><td className="p-2 text-right font-medium">${form941Data.line_items?.line_2?.toLocaleString()}</td></tr>
                    <tr className="border-b"><td className="p-2 font-medium">Line 3</td><td className="p-2">Federal income tax withheld</td><td className="p-2 text-right font-medium">${form941Data.line_items?.line_3?.toLocaleString()}</td></tr>
                    <tr className="border-b"><td className="p-2 font-medium">Line 5a</td><td className="p-2">Social security tax</td><td className="p-2 text-right font-medium">${form941Data.line_items?.line_5a_col2?.toLocaleString()}</td></tr>
                    <tr className="border-b"><td className="p-2 font-medium">Line 5c</td><td className="p-2">Medicare tax</td><td className="p-2 text-right font-medium">${form941Data.line_items?.line_5c_col2?.toLocaleString()}</td></tr>
                    <tr className="bg-emerald-50"><td className="p-2 font-medium">Line 10</td><td className="p-2">Total taxes</td><td className="p-2 text-right font-bold text-emerald-700">${form941Data.line_items?.line_10?.toLocaleString()}</td></tr>
                  </tbody>
                </table>
                <Button variant="outline" onClick={() => window.open(`${API_URL}/api/reports/form-941-pdf?year=${year}&quarter=${quarter}&company_id=${encodeURIComponent(companyId || 'default')}`, '_blank')}><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Form DE-9 */}
      {activeReport === 'de9' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Form DE-9 - California Quarterly Return</CardTitle>
              <div className="flex items-center gap-2">
                <select className="border rounded p-1 text-sm" value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                  {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select className="border rounded p-1 text-sm" value={quarter} onChange={(e) => setQuarter(parseInt(e.target.value))}>
                  {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
                </select>
                <Button size="sm" onClick={fetchDE9} disabled={loading}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!formDE9Data ? (
              <p className="text-center py-8 text-gray-500">Select year and quarter, then click "Generate"</p>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p><strong>EDD Account:</strong> {formDE9Data.employer?.edd_account}</p>
                  <p><strong>FEIN:</strong> {formDE9Data.employer?.fein}</p>
                  <p><strong>Employees:</strong> {formDE9Data.employee_count}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2">Section A - Contributions</h4>
                    <p className="text-sm">UI Contributions: ${formDE9Data.section_a?.ui_contributions?.toFixed(2)}</p>
                    <p className="text-sm">ETT Contributions: ${formDE9Data.section_a?.ett_contributions?.toFixed(2)}</p>
                    <p className="text-sm">SDI Withheld: ${formDE9Data.section_a?.sdi_withheld?.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2">Section B - PIT</h4>
                    <p className="text-sm">PIT Wages: ${formDE9Data.section_b?.pit_wages?.toLocaleString()}</p>
                    <p className="text-sm">PIT Withheld: ${formDE9Data.section_b?.pit_withheld?.toFixed(2)}</p>
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="font-bold text-emerald-800">Grand Total Due: ${formDE9Data.totals?.grand_total?.toFixed(2)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Timesheet */}
      {activeReport === 'import' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import Timesheet (CSV)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm">
              <p className="font-medium">CSV Format Required:</p>
              <code className="text-xs">employee_id, date, regular_hours, overtime_hours, double_time_hours</code>
            </div>
            <textarea
              className="w-full h-40 border rounded-lg p-3 font-mono text-sm"
              placeholder="Paste CSV data here...
Example:
employee_id, date, regular_hours, overtime_hours, double_time_hours
EMP-001, 2026-02-01, 8, 2, 0
EMP-002, 2026-02-01, 8, 0, 0"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
            />
            <Button onClick={handleImport} disabled={loading}>{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Import Data</Button>
            
            {importResult && (
              <div className={`p-3 rounded-lg ${importResult.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                <p className="font-medium">{importResult.message}</p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-sm list-disc list-inside">
                    {importResult.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AIConfigureSetup = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const questions = [
    { q: 'What type of business are you setting up?', options: ['Farm/Agriculture', 'Retail', 'Service', 'Manufacturing', 'Other'] },
    { q: 'How many employees do you have?', options: ['1-5', '6-20', '21-50', '50+'] },
    { q: 'Do you need multi-currency support?', options: ['Yes', 'No'] },
  ];

  const handleAnswer = (answer) => {
    setAnswers({...answers, [step]: answer});
    if (step < questions.length - 1) {
      setStep(step + 1);
    }
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Setup Assistant</h1>
        <p className="text-gray-500 mt-1">Answer a few questions to configure your system automatically</p>
      </div>
      
      <Card>
        <CardContent className="p-6">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>Question {step + 1} of {questions.length}</span>
              <span>{Math.round(((step + 1) / questions.length) * 100)}% complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-purple-600 h-2 rounded-full transition-all" style={{width: `${((step + 1) / questions.length) * 100}%`}}></div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{questions[step]?.q}</h3>
            <div className="grid grid-cols-2 gap-3">
              {questions[step]?.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(option)}
                  className={`p-3 border rounded-lg text-left hover:bg-purple-50 hover:border-purple-300 transition-all ${
                    answers[step] === option ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          
          {step === questions.length - 1 && answers[step] && (
            <div className="mt-6 pt-6 border-t">
              <Button className="w-full bg-purple-600 hover:bg-purple-700">
                <Brain className="w-4 h-4 mr-2" />
                Configure My System
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

const CAFarmPayrollSetup = () => {
  const navigate = useNavigate();
  const { companyId, selectedCompany } = useCompany();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [paychecks, setPaychecks] = useState([]);
  const [payRegister, setPayRegister] = useState(null);
  const [message, setMessage] = useState(null);
  
  const [employerForm, setEmployerForm] = useState({
    company_name: selectedCompany?.name || '',
    ein: '', state_ein: '', farm_ein: '',
    address: '', city: '', state: 'CA', zip: '',
    pay_frequency: 'weekly', h2a_registered: false, labor_contractor_license: ''
  });

  const [timeEntryForm, setTimeEntryForm] = useState({
    employee_id: '', date: new Date().toISOString().split('T')[0],
    regular_hours: 8, overtime_hours: 0, double_time_hours: 0,
    pieces: 0, piece_rate: 0, job_code: '', notes: ''
  });

  const steps = [
    { num: 1, title: 'Employer Setup', desc: 'Company & Tax' },
    { num: 2, title: 'Time Entry', desc: 'Hours & Pieces' },
    { num: 3, title: 'Calculate', desc: 'Tax & Progress' },
    { num: 4, title: 'Review', desc: 'Paychecks' },
    { num: 5, title: 'Post', desc: 'Annual Records' }
  ];

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const [empRes, timeRes] = await Promise.all([
        fetch(`${API_URL}/api/ca-payroll/employees${params}`),
        fetch(`${API_URL}/api/ca-payroll/time-entries${params}`)
      ]);
      if (empRes.ok) setEmployees(await empRes.json());
      if (timeRes.ok) setTimeEntries(await timeRes.json());
    } catch (err) { console.error(err); }
  };

  const handleEmployerSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/employers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...employerForm, company_id: companyId })
      });
      if (res.ok) { setMessage({ type: 'success', text: 'Employer setup saved!' }); setCurrentStep(2); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { setMessage({ type: 'error', text: 'Failed to save' }); }
    finally { setLoading(false); }
  };

  const handleTimeEntry = async () => {
    if (!timeEntryForm.employee_id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/time-entries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...timeEntryForm, company_id: companyId })
      });
      if (res.ok) { fetchData(); setTimeEntryForm(prev => ({ ...prev, regular_hours: 8, overtime_hours: 0 })); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const runCalculation = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/calculate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, pay_period_start: new Date(Date.now() - 7*24*60*60*1000).toISOString().split('T')[0], pay_period_end: new Date().toISOString().split('T')[0], pay_date: new Date().toISOString().split('T')[0] })
      });
      if (res.ok) { const data = await res.json(); setPaychecks(data.calculations || []); setPayRegister(data); setCurrentStep(4); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const postAnnual = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/post-annual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, paychecks: paychecks.map(p => p.paycheck_id) })
      });
      if (res.ok) { setMessage({ type: 'success', text: 'Posted to annual records!' }); setCurrentStep(1); fetchData(); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tractor className="w-6 h-6 text-emerald-600" />California Farm Payroll
          </h1>
          <p className="text-gray-500 mt-1">Agricultural payroll for CA farms</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/setup')}><ChevronRight className="w-4 h-4 mr-1 rotate-180" />Back</Button>
      </div>

      {message && <div className={`p-3 rounded-lg ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{message.text}</div>}

      {/* Steps */}
      <Card><CardContent className="p-4">
        <div className="flex items-center justify-between">
          {steps.map((step, idx) => (
            <div key={step.num} className="flex items-center">
              <button onClick={() => setCurrentStep(step.num)} className={`flex flex-col items-center ${currentStep === step.num ? 'text-emerald-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${currentStep > step.num ? 'bg-emerald-500' : currentStep === step.num ? 'bg-emerald-600' : 'bg-gray-300'}`}>
                  {currentStep > step.num ? <CheckCircle className="w-5 h-5" /> : step.num}
                </div>
                <span className="mt-1 text-xs font-medium">{step.title}</span>
              </button>
              {idx < steps.length - 1 && <div className={`w-16 h-0.5 mx-2 ${currentStep > step.num ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Step 1 */}
      {currentStep === 1 && (
        <Card><CardHeader><CardTitle>Employer Setup</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium">Company Name</label><Input value={employerForm.company_name} onChange={(e) => setEmployerForm({...employerForm, company_name: e.target.value})} /></div>
              <div><label className="text-sm font-medium">Federal EIN</label><Input placeholder="XX-XXXXXXX" value={employerForm.ein} onChange={(e) => setEmployerForm({...employerForm, ein: e.target.value})} /></div>
              <div><label className="text-sm font-medium">CA State EIN</label><Input value={employerForm.state_ein} onChange={(e) => setEmployerForm({...employerForm, state_ein: e.target.value})} /></div>
              <div><label className="text-sm font-medium">Farm EIN</label><Input value={employerForm.farm_ein} onChange={(e) => setEmployerForm({...employerForm, farm_ein: e.target.value})} /></div>
              <div className="col-span-2"><label className="text-sm font-medium">Address</label><Input value={employerForm.address} onChange={(e) => setEmployerForm({...employerForm, address: e.target.value})} /></div>
              <div><label className="text-sm font-medium">City</label><Input value={employerForm.city} onChange={(e) => setEmployerForm({...employerForm, city: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium">State</label><Input value="CA" disabled /></div>
                <div><label className="text-sm font-medium">ZIP</label><Input value={employerForm.zip} onChange={(e) => setEmployerForm({...employerForm, zip: e.target.value})} /></div>
              </div>
              <div><label className="text-sm font-medium">Pay Frequency</label>
                <select className="w-full border rounded-md p-2" value={employerForm.pay_frequency} onChange={(e) => setEmployerForm({...employerForm, pay_frequency: e.target.value})}>
                  <option value="weekly">Weekly</option><option value="biweekly">Bi-Weekly</option><option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={employerForm.h2a_registered} onChange={(e) => setEmployerForm({...employerForm, h2a_registered: e.target.checked})} /><label>H-2A Registered</label></div>
            </div>
            <div className="flex justify-end pt-4 border-t"><Button onClick={handleEmployerSubmit} disabled={loading}>{loading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Save & Continue<ArrowRight className="w-4 h-4 ml-2" /></Button></div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 */}
      {currentStep === 2 && (
        <div className="grid grid-cols-2 gap-4">
          <Card><CardHeader><CardTitle>Add Time Entry</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Employee</label>
                <select className="w-full border rounded-md p-2" value={timeEntryForm.employee_id} onChange={(e) => setTimeEntryForm({...timeEntryForm, employee_id: e.target.value})}>
                  <option value="">Select...</option>{employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name}</option>)}
                </select>
              </div>
              <div><label className="text-sm font-medium">Date</label><Input type="date" value={timeEntryForm.date} onChange={(e) => setTimeEntryForm({...timeEntryForm, date: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-sm font-medium">Regular</label><Input type="number" value={timeEntryForm.regular_hours} onChange={(e) => setTimeEntryForm({...timeEntryForm, regular_hours: parseFloat(e.target.value)||0})} /></div>
                <div><label className="text-sm font-medium">OT (1.5x)</label><Input type="number" value={timeEntryForm.overtime_hours} onChange={(e) => setTimeEntryForm({...timeEntryForm, overtime_hours: parseFloat(e.target.value)||0})} /></div>
                <div><label className="text-sm font-medium">DT (2x)</label><Input type="number" value={timeEntryForm.double_time_hours} onChange={(e) => setTimeEntryForm({...timeEntryForm, double_time_hours: parseFloat(e.target.value)||0})} /></div>
              </div>
              <div><label className="text-sm font-medium">Job Code</label><Input placeholder="HARVEST, PRUNE" value={timeEntryForm.job_code} onChange={(e) => setTimeEntryForm({...timeEntryForm, job_code: e.target.value})} /></div>
              <Button onClick={handleTimeEntry} disabled={loading} className="w-full"><Plus className="w-4 h-4 mr-2" />Add Entry</Button>
            </CardContent>
          </Card>
          <Card><CardHeader><CardTitle>Recent Entries ({timeEntries.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {timeEntries.map((e, i) => (<div key={i} className="p-2 bg-slate-50 rounded flex justify-between"><span>{employees.find(emp => emp.employee_id === e.employee_id)?.name || e.employee_id}</span><span className="font-mono">{e.regular_hours}h + {e.overtime_hours}h OT</span></div>))}
              </div>
              <div className="flex justify-between pt-4 border-t mt-4">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>Back</Button>
                <Button onClick={() => setCurrentStep(3)}>Continue<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3 */}
      {currentStep === 3 && (
        <Card><CardHeader><CardTitle>Calculate Payroll</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center p-4 bg-slate-50 rounded-lg">
              <div><p className="text-2xl font-bold text-sky-600">{employees.length}</p><p className="text-sm text-gray-500">Employees</p></div>
              <div><p className="text-2xl font-bold text-emerald-600">{timeEntries.length}</p><p className="text-sm text-gray-500">Entries</p></div>
              <div><p className="text-2xl font-bold text-amber-600">{timeEntries.reduce((s,e)=>s+(e.regular_hours||0)+(e.overtime_hours||0),0).toFixed(1)}h</p><p className="text-sm text-gray-500">Total Hours</p></div>
            </div>
            <div className="p-3 bg-sky-50 rounded-lg text-sm text-sky-700">
              <p className="font-medium">CA Tax Calculations Include:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Federal Income Tax</li><li>Social Security (6.2%) & Medicare (1.45%)</li><li>CA State Income Tax</li><li>CA SDI (1.1%)</li>
              </ul>
            </div>
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>Back</Button>
              <Button onClick={runCalculation} disabled={loading}>{loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}Run Calculation</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 */}
      {currentStep === 4 && (
        <Card><CardHeader><CardTitle>Paycheck Preview</CardTitle></CardHeader>
          <CardContent>
            {paychecks.length === 0 ? <p className="text-gray-500 text-center py-8">No paychecks generated</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100"><tr><th className="p-2 text-left">Employee</th><th className="p-2 text-right">Gross</th><th className="p-2 text-right">Fed Tax</th><th className="p-2 text-right">State</th><th className="p-2 text-right">FICA</th><th className="p-2 text-right font-bold">Net</th></tr></thead>
                <tbody className="divide-y">
                  {paychecks.map((p,i) => (<tr key={i}><td className="p-2">{p.employee_name}</td><td className="p-2 text-right">${(p.gross_pay?.total_gross || 0).toLocaleString()}</td><td className="p-2 text-right text-red-600">-${(p.deductions?.federal_withholding || 0).toLocaleString()}</td><td className="p-2 text-right text-red-600">-${(p.deductions?.ca_state_withholding || 0).toLocaleString()}</td><td className="p-2 text-right text-red-600">-${((p.deductions?.social_security || 0) + (p.deductions?.medicare || 0)).toLocaleString()}</td><td className="p-2 text-right font-bold text-emerald-600">${(p.net_pay || 0).toLocaleString()}</td></tr>))}
                </tbody>
              </table>
            )}
            {payRegister?.summary && <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
              <div className="p-3 bg-slate-50 rounded text-center"><p className="text-xl font-bold">${(payRegister.summary?.total_gross || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Gross</p></div>
              <div className="p-3 bg-red-50 rounded text-center"><p className="text-xl font-bold text-red-600">${(payRegister.summary?.total_deductions || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Taxes</p></div>
              <div className="p-3 bg-amber-50 rounded text-center"><p className="text-xl font-bold text-amber-600">{payRegister.summary?.employees || 0}</p><p className="text-xs text-gray-500">Employees</p></div>
              <div className="p-3 bg-emerald-50 rounded text-center"><p className="text-xl font-bold text-emerald-600">${(payRegister.summary?.total_net || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Net</p></div>
            </div>}
            <div className="flex justify-between pt-4 border-t mt-4">
              <Button variant="outline" onClick={() => setCurrentStep(3)}>Back</Button>
              <Button onClick={() => setCurrentStep(5)} disabled={paychecks.length === 0}>Continue<ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5 */}
      {currentStep === 5 && (
        <Card><CardHeader><CardTitle className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-600" />Post to Annual Records</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-emerald-50 rounded-lg space-y-2">
              <p className="font-medium text-emerald-800">Pre-Post Checklist</p>
              {[{ label: 'Paychecks calculated', done: paychecks.length > 0 }, { label: 'Taxes verified', done: paychecks.length > 0 }].map((item, i) => (
                <div key={i} className="flex items-center gap-2">{item.done ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-gray-300" />}<span>{item.label}</span></div>
              ))}
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700"><AlertCircle className="w-4 h-4 inline mr-1" />Posting updates employee annual YTD records permanently.</div>
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(4)}>Back</Button>
              <Button onClick={postAnnual} disabled={loading || paychecks.length === 0} className="bg-emerald-600 hover:bg-emerald-700">{loading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Post to Annual Records</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default SetupPage;
